import type { OversightRatingByRisk } from '@risksmart-app/trpc/src/types';
import type { GetLatestOversightRatingsByRiskIdsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetLatestOversightRatingsByRiskIdsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook } from 'src/utils';

type UseGetLatestOversightRatingsByRiskIdsArgs = {
  riskIds: string[];
};

/**
 * Reshapes the service-resolved "latest inherent/residual rating per risk" rows
 * back into the GraphQL query response shape so the page hook's shared
 * client-side resolution treats both branches uniformly. The service already
 * reduced each risk leg to a single leaf, so at most one synthetic
 * uncontrolled (inherent) and one controlled (residual) row per risk is
 * emitted; the shared resolver then trivially selects it. `Id` / `TestDate` /
 * `CreatedAtTimestamp` are unused by the resolver for a single-row-per-parent
 * input, so placeholders are safe.
 */
export const mapOversightRatingsByRiskToGraphQL = (
  data: OversightRatingByRisk[]
): GetLatestOversightRatingsByRiskIdsQuery => ({
  risk_uncontrolled_oversight_result: data.flatMap((row) =>
    row.inherent
      ? [
          {
            Id: '',
            Rating: row.inherent.Rating,
            Impact: row.inherent.Impact,
            Likelihood: row.inherent.Likelihood,
            ConfigId: row.inherent.ConfigId,
            TestDate: null,
            CreatedAtTimestamp: '',
            riskParent: [{ ParentId: row.RiskId }],
          },
        ]
      : []
  ),
  risk_controlled_oversight_result: data.flatMap((row) =>
    row.residual
      ? [
          {
            Id: '',
            Rating: row.residual.Rating,
            Impact: row.residual.Impact,
            Likelihood: row.residual.Likelihood,
            ConfigId: row.residual.ConfigId,
            TestDate: null,
            CreatedAtTimestamp: '',
            riskParent: [{ ParentId: row.RiskId }],
          },
        ]
      : []
  ),
});

export const useGetLatestOversightRatingsByRiskIds = createQueryHook<
  UseGetLatestOversightRatingsByRiskIdsArgs,
  OversightRatingByRisk[],
  GetLatestOversightRatingsByRiskIdsQuery
>({
  trpcQueryOptions: (trpc, { riskIds }) =>
    trpc.frontend.oversight.ratingsByRiskIds.queryOptions({ riskIds }),
  mapTrpcDataToGraphQL: mapOversightRatingsByRiskToGraphQL,
  graphqlDocument: GetLatestOversightRatingsByRiskIdsDocument,
  graphqlVariables: ({ riskIds }) => ({ RiskIds: riskIds }),
  graphqlFetchPolicy: 'cache-and-network',
});
